import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  DocumentChartBarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  InformationCircleIcon,
  Cog8ToothIcon,
} from '@heroicons/react/24/outline';
import ReactTooltip from 'react-tooltip';
import ReportTemplate from '../../components/reports/ReportTemplate';
import DateRangePicker from '../../components/reports/DateRangePicker';
import CSVExportButton from '../../components/reports/CSVExportButton';
import { ROUTES } from '../../consts/general';
import { WAREHOUSE_INVENTORY_VALUE } from '../../consts/reports';
import { SelectDropdown } from '../../components/SelectDropdown';
import { ReportEmailSettingsSidebar } from '../../components/reports/ReportEmailSettingsSidebar';
import { toast } from 'react-toastify';
import {
  getReportEmailSettings,
  updateReportEmailSettings,
  sendReportPreviewEmail,
} from '../../slices/reportEmailSettings';
import { getReportSendHistory } from '../../slices/reportSendHistory';
import { WAREHOUSE_INVENTORY_TYPE } from '../../consts/cronTypes';
import { updateCronInformation } from '../../slices/cronSlice';
import { buildCronInterval } from '../../helpers/cronExpression';
import { useAbortableRequest } from '../../helpers/useAbortableRequest';
import { useInitialMountDelay } from '../../helpers/useInitialMountDelay';
import useDateRangeParams from '../../hooks/useDateRangeParams';
import useUrlParam from '../../hooks/useUrlParam';
import {
  isValidMonthParam,
  isValidQuarterParam,
  parseMonthParam,
  parseQuarterParam,
  serializeMonth,
  serializeQuarter,
} from '../../hooks/dateParamHelpers';

const DATE_TYPES = {
  ALL_TIME: 'all_time',
  RANGE: 'range',
  MONTH: 'month',
  QUARTER: 'quarter',
};

const PO_STATUS = {
  ALL: 'all',
  OPEN: 'open',
  CLOSED: 'closed',
};

const quarters = [
  { label: 'Q1', startMonth: 0, endMonth: 2 },
  { label: 'Q2', startMonth: 3, endMonth: 5 },
  { label: 'Q3', startMonth: 6, endMonth: 8 },
  { label: 'Q4', startMonth: 9, endMonth: 11 },
];

function monthLabel(year, monthIndex) {
  const d = new Date(year, monthIndex, 1);
  return d.toLocaleString(undefined, { month: 'long', year: 'numeric' });
}

function getMonthRange(year, monthIndex) {
  const start = new Date(year, monthIndex, 1);
  const end = new Date(year, monthIndex + 1, 0);
  const toYMD = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { startDate: toYMD(start), endDate: toYMD(end) };
}

function getQuarterStartEnd(year, qIndex) {
  const { startMonth, endMonth } = quarters[qIndex];
  const start = new Date(year, startMonth, 1);
  const end = new Date(year, endMonth + 1, 0);
  const toYMD = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { startDate: toYMD(start), endDate: toYMD(end) };
}

function getAllTimeRange() {
  const start = new Date(2020, 0, 1);
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  const toYMD = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { startDate: toYMD(start), endDate: toYMD(end) };
}

function getLast30DaysRange() {
  const today = new Date();
  const start = new Date();
  start.setDate(today.getDate() - 30);
  const toYMD = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { startDate: toYMD(start), endDate: toYMD(today) };
}

const WarehouseInventoryValueReport = ({ marginFromSidebar }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { settings: allSettings, loading: isLoadingEmailSettings } = useSelector((s) => s.reportEmailSettings);
  const emailSettings = allSettings['warehouse-inventory-value'] || {};
  const { sendHistory: allSendHistory, loading: isLoadingSendHistory } = useSelector((s) => s.reportSendHistory);
  const sendHistory = allSendHistory['warehouse-inventory-value'] || [];
  const { beginRequest, isLatest } = useAbortableRequest();
  const initialReady = useInitialMountDelay(500);
  const [isOpenSettings, setIsOpenSettings] = useState(false);

  useEffect(() => {
    ReactTooltip.rebuild();
  }, []);

  const TooltipIcon = ({ tooltipId, label }) => {
    return (
      <span className="inline-flex items-center align-middle">
        <InformationCircleIcon className="h-5 w-5 text-blue-600 hover:text-blue-900" data-tip={true} data-for={tooltipId} aria-label={label} />
        <ReactTooltip id={tooltipId} effect="solid" place="top">
          <span className="max-w-xs whitespace-normal break-words">{label}</span>
        </ReactTooltip>
      </span>
    );
  };

  const [data, setData] = useState([]);
  const [totals, setTotals] = useState({
    units_received: 0,
    units_ordered: 0,
    units_missing: 0,
    las_vegas_value: 0,
    china_value: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    ReactTooltip.rebuild();
  }, [data]);

  // Sorting
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 50;

  // Filters
  const [poSearch, setPoSearch] = useState('');
  const [debouncedPoSearch, setDebouncedPoSearch] = useState('');
  const [poStatus, setPoStatus] = useState(PO_STATUS.ALL);
  const now = new Date();
  const defaultRange = getLast30DaysRange();
  const defaultMonth = { year: now.getFullYear(), monthIndex: now.getMonth() };
  const defaultQuarter = { quarterYear: now.getFullYear(), quarterIndex: Math.floor(now.getMonth() / 3) };
  const [dateType, setDateType] = useUrlParam(
    'dateType',
    DATE_TYPES.ALL_TIME,
    (value) => Object.values(DATE_TYPES).includes(value),
  );
  const { startDate, endDate, setDateRange } = useDateRangeParams(defaultRange.startDate, defaultRange.endDate);

  // Month mode state
  const [monthParam, setMonthParam] = useUrlParam('month', serializeMonth(defaultMonth), isValidMonthParam);
  const monthYear = useMemo(
    () => parseMonthParam(monthParam, defaultMonth),
    [monthParam, defaultMonth.year, defaultMonth.monthIndex],
  );
  const monthInputRef = useRef(null);

  // Quarter mode state
  const [quarterParam, setQuarterParam] = useUrlParam('quarter', serializeQuarter(defaultQuarter), isValidQuarterParam);
  const quarter = useMemo(
    () => parseQuarterParam(quarterParam, defaultQuarter),
    [quarterParam, defaultQuarter.quarterYear, defaultQuarter.quarterIndex],
  );
  const { quarterYear, quarterIndex } = quarter;
  const [isQuarterPickerOpen, setIsQuarterPickerOpen] = useState(false);

  const columns = useMemo(
    () => [
      { key: 'po_number', label: 'PO Number', type: 'string', sortable: true, sticky: true },
      { key: 'date_received', label: 'Date Recieved', type: 'date', sortable: true },
      { key: 'units_received', label: 'Units Recieved', type: 'number', sortable: true, align: 'right' },
      { key: 'units_ordered', label: 'Units Ordered', type: 'number', sortable: true, align: 'right' },
      { key: 'units_missing', label: 'Units Missing', type: 'number', sortable: true, align: 'right' },
      { key: 'landed_cost', label: 'Landed Cost', type: 'currency', sortable: true, align: 'right' },
      { key: 'las_vegas_value', label: 'Las Vegas Value', type: 'currency', sortable: true, align: 'right' },
      { key: 'china_value', label: 'China Value', type: 'currency', sortable: true, align: 'right' },
    ],
    []
  );

  // Debounce PO search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedPoSearch(poSearch.trim()), 300);
    return () => clearTimeout(t);
  }, [poSearch]);

  const effectiveRange = useMemo(() => {
    if (dateType === DATE_TYPES.ALL_TIME) return getAllTimeRange();
    if (dateType === DATE_TYPES.RANGE) return { startDate, endDate };
    if (dateType === DATE_TYPES.MONTH) return getMonthRange(monthYear.year, monthYear.monthIndex);
    return getQuarterStartEnd(quarterYear, quarterIndex);
  }, [dateType, startDate, endDate, monthYear, quarterYear, quarterIndex]);

  const fetchData = useCallback(async () => {
    if (dateType === DATE_TYPES.RANGE && (!effectiveRange.startDate || !effectiveRange.endDate)) return;
    if (!effectiveRange.startDate || !effectiveRange.endDate) return;
    const { signal, requestId } = beginRequest();
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        startDate: effectiveRange.startDate,
        endDate: effectiveRange.endDate,
        page: String(currentPage),
        pageSize: String(pageSize),
      });
      params.append('dateType', dateType);
      if (poStatus && poStatus !== PO_STATUS.ALL) {
        params.append('poStatus', poStatus);
      }
      if (sortField) {
        params.append('sortField', sortField);
        params.append('sortDirection', sortDirection);
      }
      if (debouncedPoSearch) {
        params.append('poNumber', debouncedPoSearch);
      }

      const res = await fetch(`/api/v1/reports/${WAREHOUSE_INVENTORY_VALUE}?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        signal,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to fetch report data');
      }
      const json = await res.json();
      if (!isLatest(requestId)) return;
      setData(json.data || []);
      setTotals(
        json.totals || {
          units_received: 0,
          units_ordered: 0,
          units_missing: 0,
          las_vegas_value: 0,
          china_value: 0,
        }
      );
      setTotalPages(json.totalPages || 1);
    } catch (e) {
      if (e?.name === 'AbortError') return;
      console.error(e);
      if (!isLatest(requestId)) return;
      setError(e.message);
      setData([]);
      setTotals({
        units_received: 0,
        units_ordered: 0,
        units_missing: 0,
        las_vegas_value: 0,
        china_value: 0,
      });
      setTotalPages(1);
    } finally {
      if (isLatest(requestId)) setIsLoading(false);
    }
  }, [dateType, poStatus, effectiveRange, currentPage, pageSize, sortField, sortDirection, debouncedPoSearch, beginRequest, isLatest]);

  useEffect(() => {
    if (!initialReady) return;
    fetchData();
  }, [initialReady, fetchData]);

  // Load email settings + send history on mount
  useEffect(() => {
    dispatch(getReportEmailSettings('warehouse-inventory-value'));
    dispatch(getReportSendHistory('warehouse-inventory-value'));
  }, [dispatch]);

  const handleDateChange = ({ startDate: s, endDate: e }) => {
    setDateRange({ startDate: s, endDate: e });
    setCurrentPage(1);
  };

  // Month controls
  const goPrevMonth = () => {
    const m = monthYear.monthIndex - 1;
    if (m < 0) setMonthParam(serializeMonth({ year: monthYear.year - 1, monthIndex: 11 }));
    else setMonthParam(serializeMonth({ ...monthYear, monthIndex: m }));
    setCurrentPage(1);
  };

  const goNextMonth = () => {
    const m = monthYear.monthIndex + 1;
    if (m > 11) setMonthParam(serializeMonth({ year: monthYear.year + 1, monthIndex: 0 }));
    else setMonthParam(serializeMonth({ ...monthYear, monthIndex: m }));
    setCurrentPage(1);
  };

  const onPickMonth = (e) => {
    const value = e.target.value; // "YYYY-MM"
    const [y, mm] = value.split('-').map(Number);
    if (y && mm) {
      setMonthParam(serializeMonth({ year: y, monthIndex: mm - 1 }));
      setCurrentPage(1);
    }
  };

  // Quarter controls
  const goPrevQuarter = () => {
    const q = quarterIndex - 1;
    if (q < 0) {
      setQuarterParam(serializeQuarter({ quarterYear: quarterYear - 1, quarterIndex: 3 }));
    } else {
      setQuarterParam(serializeQuarter({ quarterYear, quarterIndex: q }));
    }
    setCurrentPage(1);
  };

  const goNextQuarter = () => {
    const q = quarterIndex + 1;
    if (q > 3) {
      setQuarterParam(serializeQuarter({ quarterYear: quarterYear + 1, quarterIndex: 0 }));
    } else {
      setQuarterParam(serializeQuarter({ quarterYear, quarterIndex: q }));
    }
    setCurrentPage(1);
  };

  const currentQuarterLabel = `${quarters[quarterIndex].label} ${quarterYear}`;

  const handleSort = ({ field, direction }) => {
    setSortField(field);
    setSortDirection(direction);
    setCurrentPage(1);
  };

  const handleBackToReports = () => navigate(ROUTES.REPORTS);

  const handleSuccessSubmit = async (formData) => {
    const payload = {
      emailRecipients: formData?.emailRecipients || [],
      scheduleType: formData?.schedule || null,
      dayOfMonth: formData?.dayOfMonth ?? null,
      slackChannelId: formData?.slackChannelId ?? null,
      dayOfWeek: formData?.dayOfWeek ?? null,
      timeOfDay: formData?.timeOfDay ?? null,
      emailMessage: formData?.emailMessage ?? null,
      emailSendPreviewTo: formData?.emailSendPreviewTo ?? null,
    };

    try {
      await dispatch(updateReportEmailSettings({ reportType: 'warehouse-inventory-value', ...payload })).unwrap();
      const cronInterval = buildCronInterval({
        scheduleType: payload.scheduleType,
        dayOfMonth: payload.dayOfMonth,
        dayOfWeek: payload.dayOfWeek,
        timeOfDay: payload.timeOfDay,
      });
      if (!cronInterval) {
        toast.error('Invalid schedule settings (cron expression could not be built)', { position: 'bottom-right' });
        return;
      }
      const cronData = {
        id: emailSettings?.cronId,
        type: emailSettings?.cronType || WAREHOUSE_INVENTORY_TYPE,
        cronInterval,
      };
      await dispatch(updateCronInformation({ singleData: cronData })).unwrap();
      toast.success('Settings saved successfully', { position: 'bottom-right' });
      setIsOpenSettings(false);
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Failed to save settings', { position: 'bottom-right' });
    }
  };

  const sendPreviewEmail = async (formData) => {
    try {
      const payload = {
        email: formData?.emailSendPreviewTo,
        emailMessage: formData?.emailMessage || '',
        startDate: effectiveRange.startDate,
        endDate: effectiveRange.endDate,
        dateType: DATE_TYPES.ALL_TIME,
        scheduleType: formData?.type || null,
        sortField: sortField || null,
        sortDirection: sortDirection || null,
        poNumber: debouncedPoSearch || null,
      };
      if (poStatus && poStatus !== PO_STATUS.ALL) {
        payload.poStatus = poStatus;
      }
      await dispatch(sendReportPreviewEmail({ reportType: 'warehouse-inventory-value', ...payload })).unwrap();
      await dispatch(getReportSendHistory('warehouse-inventory-value')).unwrap();
      toast.success('Preview email sent', { position: 'bottom-right' });
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Failed to send preview', { position: 'bottom-right' });
    }
  };

  const sendPreviewSlack = async (formData) => {
    try {
      const payload = {
        slackChannelId: formData?.slackChannelId,
        emailMessage: formData?.emailMessage || '',
        startDate: effectiveRange.startDate,
        endDate: effectiveRange.endDate,
        dateType: DATE_TYPES.ALL_TIME,
        scheduleType: formData?.type || null,
        sortField: sortField || null,
        sortDirection: sortDirection || null,
        poNumber: debouncedPoSearch || null,
      };
      if (poStatus && poStatus !== PO_STATUS.ALL) {
        payload.poStatus = poStatus;
      }
      await dispatch(sendReportPreviewEmail({ reportType: 'warehouse-inventory-value', ...payload })).unwrap();
      await dispatch(getReportSendHistory('warehouse-inventory-value')).unwrap();
      toast.success('Slack preview sent', { position: 'bottom-right' });
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Failed to send slack preview', { position: 'bottom-right' });
    }
  };

  const formatCurrency = (value) => {
    const num = Number(value);
    if (Number.isNaN(num)) return '';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
  };

  const formatRangeCurrency = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const s = String(value).trim();
    const parts = s.split(' - ').map((x) => String(x).trim());
    if (parts.length === 1) return formatCurrency(parts[0]);
    const a = Number(parts[0]);
    const b = Number(parts[1]);
    if (Number.isNaN(a) || Number.isNaN(b)) return s;
    const aFixed = Number(a.toFixed(2));
    const bFixed = Number(b.toFixed(2));
    // If one side is 0 and the other is > 0, treat as missing-cost and show the non-zero value.
    if (aFixed === 0 && bFixed > 0) return formatCurrency(bFixed);
    if (bFixed === 0 && aFixed > 0) return formatCurrency(aFixed);
    if (aFixed === bFixed) return formatCurrency(aFixed);
    return `${formatCurrency(aFixed)} - ${formatCurrency(bFixed)}`;
  };

  const formatRangeDate = (value) => {
    if (!value) return '';
    const s = String(value).trim();
    const toPretty = (ymd) => {
      const t = String(ymd).trim();
      if (t.includes(',') && (t.includes('Jan') || t.includes('Feb') || t.includes('Mar') || t.includes('Apr'))) return t;
      const parts = t.split('-').map((x) => String(x).trim());
      if (parts.length !== 3) return t;
      const [yyyy, mm, dd] = parts;
      const d = new Date(`${yyyy}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}T00:00:00`);
      if (Number.isNaN(d.getTime())) return t;
      return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    };
    const rangeParts = s.split(' - ');
    if (rangeParts.length === 1) return toPretty(rangeParts[0]);
    const left = toPretty(rangeParts[0]);
    const right = toPretty(rangeParts[1]);
    if (left === right) return left;
    return `${left} - ${right}`;
  };

  const renderRows = () => {
    const totalRow = (
      <tr key="__total" className="bg-gray-50">
        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">Total</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700" />
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-right">
          {Number(totals?.units_received || 0).toLocaleString()}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-right">
          {Number(totals?.units_ordered || 0).toLocaleString()}
        </td>
        <td
          className={`px-6 py-4 whitespace-nowrap text-sm text-right ${
            Number(totals?.units_missing || 0) > 0 ? 'text-red-500' : 'text-gray-900'
          }`}
        >
          {Number(totals?.units_missing || 0).toLocaleString()}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-right" />
        <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-right ${Number(totals?.las_vegas_value || 0) > 0 ? 'text-green-700' : 'text-gray-900'}`}>{formatCurrency(totals?.las_vegas_value || 0)}</td>
        <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-right ${Number(totals?.china_value || 0) > 0 ? 'text-red-500' : 'text-gray-900'}`}>{formatCurrency(totals?.china_value || 0)}</td>
      </tr>
    );

    const rows = (data || []).map((row, idx) => (
      <tr key={`${row.po_number || 'po'}-${idx}`} className="bg-white">
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {row?.po_id ? (
            <a
              href={`${ROUTES.SHIPPING}?search=${encodeURIComponent(String(row.po_number || ''))}#${row.po_id}`}
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 hover:underline"
              title="Open in Shipping"
            >
              {row.po_number}
            </a>
          ) : (
            row.po_number
          )}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatRangeDate(row.date_received)}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{Number(row.units_received || 0).toLocaleString()}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{Number(row.units_ordered || 0).toLocaleString()}</td>
        <td
          className={`px-6 py-4 whitespace-nowrap text-sm text-right ${
            Number(row.units_missing || 0) > 0 ? 'text-red-500' : 'text-gray-900'
          }`}
        >
          {Number(row.units_missing || 0).toLocaleString()}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatRangeCurrency(row.landed_cost)}</td>
        <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right ${Number(row.las_vegas_value || 0) > 0 ? 'text-green-700' : 'text-gray-900'}`}>{formatCurrency(row.las_vegas_value)}</td>
        <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right ${Number(row.china_value || 0) > 0 ? 'text-red-500' : 'text-gray-900'}`}>{formatCurrency(row.china_value)}</td>
      </tr>
    ));

    return [totalRow, ...rows];
  };

  const filtersComponent = (
    <div className="contents">
      <div className="flex flex-row items-center gap-2">
        <input
          type="text"
          value={poSearch}
          onChange={(e) => {
            setPoSearch(e.target.value);
            setCurrentPage(1);
          }}
          placeholder="Search PO #"
          className="w-40 sm:w-[160px] rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
        />
      </div>

      <div className="flex flex-row items-center gap-2">
        <SelectDropdown
          selectClassname="!w-40 !bg-white"
          options={[
            { label: 'All PO Statuses', value: PO_STATUS.ALL },
            { label: 'Open POs', value: PO_STATUS.OPEN },
            { label: 'Closed POs', value: PO_STATUS.CLOSED },
          ]}
          value={poStatus}
          onChange={(value) => {
            setPoStatus(value);
            setCurrentPage(1);
          }}
        />
      </div>

      <div className="flex flex-row items-center gap-2">
        <SelectDropdown
          selectClassname="!w-36 !bg-white"
          options={[
            { label: 'All Time', value: DATE_TYPES.ALL_TIME },
            { label: 'Date Range', value: DATE_TYPES.RANGE },
            { label: 'Month', value: DATE_TYPES.MONTH },
            { label: 'Quarter', value: DATE_TYPES.QUARTER },
          ]}
          value={dateType}
          onChange={(value) => {
            setDateType(value);
            setCurrentPage(1);
          }}
        />
      </div>

      {dateType === DATE_TYPES.RANGE && (
        <DateRangePicker startDate={startDate} endDate={endDate} onDateChange={handleDateChange} label="Date Received Range" />
      )}

      {dateType === DATE_TYPES.MONTH && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={goPrevMonth}
            className="inline-flex items-center justify-center rounded-md border border-gray-300 p-2 hover:bg-gray-50"
            title="Previous month"
          >
            <ChevronLeftIcon className="h-4 w-4 text-gray-600" />
          </button>
          <button
            type="button"
            className="px-3 py-2 rounded-md border border-gray-300 bg-white text-sm w-[140px]"
            onClick={() => {
              const el = monthInputRef.current;
              if (el && typeof el.showPicker === 'function') {
                try {
                  el.showPicker();
                } catch (e) {
                  el.click();
                }
              } else if (el) {
                el.click();
              }
            }}
            title="Pick month and year"
          >
            {monthLabel(monthYear.year, monthYear.monthIndex)}
          </button>
          <input
            type="month"
            ref={monthInputRef}
            className="absolute opacity-0 w-0 h-0 pointer-events-none"
            onChange={onPickMonth}
            value={`${monthYear.year}-${String(monthYear.monthIndex + 1).padStart(2, '0')}`}
          />
          <button
            type="button"
            onClick={goNextMonth}
            className="inline-flex items-center justify-center rounded-md border border-gray-300 p-2 hover:bg-gray-50"
            title="Next month"
          >
            <ChevronRightIcon className="h-4 w-4 text-gray-600" />
          </button>
        </div>
      )}

      {dateType === DATE_TYPES.QUARTER && (
        <div className="relative flex items-center gap-2">
          <button
            type="button"
            onClick={goPrevQuarter}
            className="inline-flex items-center justify-center rounded-md border border-gray-300 p-2 hover:bg-gray-50"
            title="Previous quarter"
          >
            <ChevronLeftIcon className="h-4 w-4 text-gray-600" />
          </button>
          <button
            type="button"
            className="px-3 py-2 rounded-md border border-gray-300 bg-white text-sm w-[110px]"
            onClick={() => setIsQuarterPickerOpen((v) => !v)}
            title="Pick quarter and year"
          >
            {currentQuarterLabel}
          </button>
          <button
            type="button"
            onClick={goNextQuarter}
            className="inline-flex items-center justify-center rounded-md border border-gray-300 p-2 hover:bg-gray-50"
            title="Next quarter"
          >
            <ChevronRightIcon className="h-4 w-4 text-gray-600" />
          </button>

          {isQuarterPickerOpen && (
            <div className="absolute top-full mt-2 z-10 bg-white border border-gray-200 rounded-md shadow w-64 p-3">
              <div className="flex items-center justify-between mb-3">
                <button className="px-2 py-1 border rounded text-sm" onClick={() => setQuarterParam(serializeQuarter({ quarterYear: quarterYear - 1, quarterIndex }))}>
                  {quarterYear - 1}
                </button>
                <div className="font-medium">{quarterYear}</div>
                <button className="px-2 py-1 border rounded text-sm" onClick={() => setQuarterParam(serializeQuarter({ quarterYear: quarterYear + 1, quarterIndex }))}>
                  {quarterYear + 1}
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {quarters.map((q, idx) => (
                  <button
                    key={q.label}
                    className={`px-2 py-2 border rounded text-sm ${idx === quarterIndex ? 'bg-blue-50 border-blue-300' : 'bg-white'}`}
                    onClick={() => {
                      setQuarterParam(serializeQuarter({ quarterYear, quarterIndex: idx }));
                      setIsQuarterPickerOpen(false);
                      setCurrentPage(1);
                    }}
                  >
                    {q.label} {quarterYear}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div>
        <Cog8ToothIcon className="h-5 w-5 text-primary cursor-pointer" onClick={() => setIsOpenSettings(true)} />
      </div>
    </div>
  );

  const exportButton = (
    <CSVExportButton
      data={data}
      columns={[
        { key: 'po_number', label: 'PO Number' },
        { key: 'date_received', label: 'Date Recieved' },
        { key: 'units_received', label: 'Units Recieved' },
        { key: 'units_ordered', label: 'Units Ordered' },
        { key: 'units_missing', label: 'Units Missing' },
        { key: 'landed_cost', label: 'Landed Cost' },
        { key: 'las_vegas_value', label: 'Las Vegas Value' },
        { key: 'china_value', label: 'China Value' },
      ]}
      filename={`${WAREHOUSE_INVENTORY_VALUE}`}
      onExport={async () => {
        const params = new URLSearchParams({
          startDate: effectiveRange.startDate,
          endDate: effectiveRange.endDate,
        });
        params.append('dateType', dateType);
        if (poStatus && poStatus !== PO_STATUS.ALL) {
          params.append('poStatus', poStatus);
        }
        if (sortField) {
          params.append('sortField', sortField);
          params.append('sortDirection', sortDirection);
        }
        if (debouncedPoSearch) {
          params.append('poNumber', debouncedPoSearch);
        }
        const res = await fetch(`/api/v1/reports/${WAREHOUSE_INVENTORY_VALUE}/export?${params.toString()}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || 'Export failed');
        }
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        const contentDisposition = res.headers.get('content-disposition');
        const exportFilename = contentDisposition
          ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
          : `${WAREHOUSE_INVENTORY_VALUE}_${new Date().toISOString().slice(0, 10)}.csv`;
        link.href = url;
        link.download = exportFilename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        return { success: true, handled: true };
      }}
    />
  );

  const paginationComponent =
    totalPages > 1 ? (
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-700">
          Page {currentPage} of {totalPages}
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:bg-gray-100 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Previous
          </button>
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:bg-gray-100 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Next
          </button>
        </div>
      </div>
    ) : null;

  const breadcrumbs = [
    { label: 'Reports', icon: <DocumentChartBarIcon className="h-5 w-5 text-gray-400" /> },
    { label: '' },
  ];

  return (
    <>
      <ReportTemplate
        reportId={WAREHOUSE_INVENTORY_VALUE}
        description="Warehouse inventory value by PO (received/ordered/missing units + landed cost + Las Vegas/China value). Includes OPEN + CLOSED POs. Filtered by day 1 of receiving."
        breadcrumbs={breadcrumbs}
        onBack={handleBackToReports}
        marginFromSidebar={marginFromSidebar}
        isLoading={isLoading}
        error={error}
        filters={filtersComponent}
        exportButton={exportButton}
        data={data}
        columns={columns}
        pagination={paginationComponent}
        emptyMessage="No inventory was received."
        onSort={handleSort}
        sortField={sortField}
        cronType={emailSettings?.cronType || null}
        sortDirection={sortDirection}
        enableSorting={true}
      >
        {renderRows()}
      </ReportTemplate>

      <ReportEmailSettingsSidebar
        reportType={'warehouse-inventory-value'}
        isOpen={isOpenSettings}
        marginFromSidebar={marginFromSidebar}
        onClose={() => setIsOpenSettings(false)}
        isLoading={isLoadingEmailSettings}
        onSuccessSubmit={handleSuccessSubmit}
        sendPreviewEmail={sendPreviewEmail}
        sendPreviewSlack={sendPreviewSlack}
        defaultSettings={emailSettings || {}}
        sendHistory={sendHistory}
        isLoadingSendHistory={isLoadingSendHistory}
        downloadFilenamePrefix="warehouse-inventory-value"
      />
    </>
  );
};

export default WarehouseInventoryValueReport;
